import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { apiFetch } from '../../utils/apiClient';

interface SyncLog {
  id: number;
  sync_type: string;
  status: string;
  book_title: string;
  book_isbn_13: string;
  vendor_name: string;
  shopify_product_id?: string;
  error_message?: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  retry_count: number;
}

const AdminSyncLogs: React.FC = () => {
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await apiFetch('/api/sync-logs/');
      if (!response.ok) throw new Error('Failed to fetch listing logs');
      const data = await response.json();
      setLogs(data.results || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load listing logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Listing logs</h1>
            <p className="text-sm text-gray-600 mt-1">BookMarketLk listing history and errors</p>
          </div>
          <Link
            to="/admin"
            className="text-blue-600 active:text-blue-700 font-medium inline-flex items-center min-h-[44px] touch-manipulation"
          >
            Back to Admin
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading listing logs...</p>
          </div>
        ) : error ? (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 sm:p-12 text-center">
            <p className="text-gray-600">No listing logs found.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {logs.map((log) => (
                <li key={log.id} className="px-3 sm:px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                          {log.book_title || '(No book)'}
                        </p>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium self-start ${
                            log.status === 'success'
                              ? 'bg-green-100 text-green-800'
                              : log.status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {log.status}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium self-start bg-blue-50 text-blue-700">
                          {log.sync_type}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-500">
                        <p className="truncate">Vendor: {log.vendor_name || '—'}</p>
                        <p className="truncate">ISBN: {log.book_isbn_13 || '—'}</p>
                        <p className="truncate">Retries: {log.retry_count}</p>
                        {typeof log.duration_seconds === 'number' ? (
                          <p className="truncate">Duration: {log.duration_seconds.toFixed(2)}s</p>
                        ) : null}
                      </div>
                      {log.error_message ? (
                        <p className="mt-2 text-xs sm:text-sm text-red-700 whitespace-pre-wrap">{log.error_message}</p>
                      ) : null}
                    </div>
                    <div className="text-right text-xs sm:text-sm text-gray-500">
                      <div>{new Date(log.started_at).toLocaleString()}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminSyncLogs;

